import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Gamepad2, Headphones, Square, Volume2 } from "lucide-react";

import type { BlsChannel } from "../../application/useBlsChannel";
import type { BlsHaptics } from "../../application/useBlsHaptics";
import { useBlsTones } from "../../application/useBlsTones";
import BlsStimulus from "./BlsStimulus";

type Props = {
    channel: BlsChannel;
    /**
     * The controllers, driven from the stage rather than here: the therapist
     * needs to know about them before a set starts, while this view only
     * exists during one.
     */
    haptics: BlsHaptics;
};

function tactileCopy({ supported, controllers }: BlsHaptics): string {
    if (!supported) return "emdr.patient.tactileUnsupported";
    if (controllers === 0) return "emdr.patient.tactileConnect";
    return controllers === 1 ? "emdr.patient.tactileOne" : "emdr.patient.tactileTwo";
}

/**
 * What the patient sees during a set: the field, edge to edge, and a way out.
 *
 * The stop is always within reach — a large button and the Escape key —
 * because a patient in the middle of reprocessing must not have to ask to
 * stop. There is nothing else to press: speed and colour are the therapist's,
 * and every extra control is a reason to look away from the dot.
 */
export default function PatientBlsView({ channel, haptics }: Props) {
    const { t } = useTranslation();
    const { state, cyclesNow, halt } = channel;
    const tones = useBlsTones({ state, cyclesNow, enabled: true });

    useEffect(() => {
        const onKey = (event: KeyboardEvent) => {
            if (event.key === "Escape") halt();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [halt]);

    if (!state) return null;
    const audible = state.settings.mode !== "visual";

    return (
        <section
            aria-label={t("emdr.patient.label")}
            className="relative flex size-full min-h-0 flex-col overflow-hidden rounded-2xl"
            data-testid="patient-bls"
        >
            <BlsStimulus state={state} cyclesNow={cyclesNow} className="min-h-0 flex-1" />

            <div className="pointer-events-none absolute inset-x-0 top-3 flex flex-col items-center gap-2 px-3">
                {audible && (
                    <p className="flex items-center gap-2 rounded-full bg-room-base/80 px-3 py-1.5 text-xs text-room-ink-muted">
                        <Headphones size={14} aria-hidden="true" />
                        {t("emdr.patient.headphones")}
                    </p>
                )}
                {state.settings.tactile && (
                    <p className="flex items-center gap-2 rounded-full bg-room-base/80 px-3 py-1.5 text-xs text-room-ink-muted">
                        <Gamepad2 size={14} aria-hidden="true" />
                        {t(tactileCopy(haptics))}
                    </p>
                )}
                {!state.running && (
                    <p role="status" className="rounded-full bg-room-base/80 px-3 py-1.5 text-xs text-room-ink">
                        {t("emdr.patient.paused")}
                    </p>
                )}
            </div>

            <div className="absolute inset-x-0 bottom-4 flex justify-center gap-2 px-3">
                {audible && tones.soundBlocked && (
                    <button type="button" onClick={() => void tones.enableSound()} className="room-control">
                        <Volume2 size={16} aria-hidden="true" />
                        <span>{t("emdr.patient.enableSound")}</span>
                    </button>
                )}
                {state.running && (
                    <button
                        type="button"
                        onClick={halt}
                        className="room-control room-control-end min-h-12 px-6 text-base"
                    >
                        <Square size={16} aria-hidden="true" />
                        <span>{t("emdr.patient.stop")}</span>
                    </button>
                )}
            </div>
        </section>
    );
}
